// Dedicated Studio profile — partnership/production operations view. Does
// NOT reuse the generic performer/person CRM 360 preview: no fan purchases,
// no cast/actor collaboration wording. Prioritizes partnership status, shoot
// history/productions, catalog items, decision-maker contacts, contract/
// legal status, and communication follow-ups.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  X as XIcon, Building2, Mail, Phone, Globe, MapPin, FileText,
  Film, Plus, Check, Trash2, Sparkles, MessageCircle, Pencil, ExternalLink, Image as ImageIcon,
} from "lucide-react";
import { api } from "../../lib/api";
import { STATUS_LABEL, FOLLOWUP_KINDS, FU_DIRECTIONS, FU_OUTCOMES, getRecordStatuses, primaryStatus, STATUS_HEX } from "./constants";
import { formatRelative } from "./helpers";
import OperationsPulse from "../../components/OperationsPulse";

const DEAL_STATUS_LABEL = {
  new_lead: "New lead", negotiating: "Negotiating", confirmed: "Confirmed",
  scheduled: "Scheduled", shooting: "Shooting", wrapped: "Wrapped",
  edited: "Edited", released: "Released", paid: "Paid",
  rejected: "Rejected", archived: "Archived",
};
const DEAL_STATUS_COLOR = {
  new_lead: "#fbbf24", negotiating: "#f97316", confirmed: "#22c55e",
  scheduled: "#06b6d4", shooting: "#a855f7", wrapped: "#14b8a6",
  edited: "#84cc16", released: "#10b981", paid: "#16a34a",
  rejected: "#dc2626", archived: "#6b7280",
};

export default function StudioProfile({ person, onClose, onEdit, onDelete, onLocalUpdate, canDelete = true }) {
  const [deals, setDeals] = useState([]);
  const [catalogVideos, setCatalogVideos] = useState([]);
  const [loadingDeals, setLoadingDeals] = useState(true);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const [logForm, setLogForm] = useState({ kind: "email", direction: "outbound", outcome: "neutral", summary: "", note: "" });
  const [savingFu, setSavingFu] = useState(false);

  useEffect(() => {
    let active = true;
    api.get(`/api/studios/by-crm/${person.id}`)
      .then((r) => { if (active) setDeals(r.data || []); })
      .catch(() => { if (active) setDeals([]); })
      .finally(() => { if (active) setLoadingDeals(false); });
    api.get(`/api/videos/by-studio/${person.id}`)
      .then((r) => { if (active) setCatalogVideos(r.data || []); })
      .catch(() => { if (active) setCatalogVideos([]); })
      .finally(() => { if (active) setLoadingCatalog(false); });
    api.get("/api/ai-manager/contact-recommendations")
      .then((r) => {
        if (!active) return;
        const match = (r.data?.recommendations || []).find((rec) => rec.contact_id === person.id);
        setAiSuggestion(match || null);
      })
      .catch(() => { if (active) setAiSuggestion(null); });
    return () => { active = false; };
  }, [person.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const recordStatuses = getRecordStatuses(person);
  const primary = primaryStatus(recordStatuses);
  const fuSorted = [...(person.follow_ups || [])].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const sceneCount = deals.reduce((sum, d) => sum + (d.scenes || []).length, 0);
  const openDeals = deals.filter((d) => !["paid", "rejected", "archived"].includes(d.status)).length;
  const hasContract = Boolean(person.studio_contract_template_url);
  const hasRelease = Boolean(person.studio_model_release_url);

  const persistFollowUps = async (next) => {
    try {
      const updated = await api.put(`/api/crm/${person.id}`, { follow_ups: next });
      onLocalUpdate(updated.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const addFollowUp = async () => {
    const summary = (logForm.summary || "").trim();
    if (!summary) return toast.error("Summary is required");
    setSavingFu(true);
    const newFu = {
      id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      kind: logForm.kind, direction: logForm.direction, outcome: logForm.outcome,
      summary, note: (logForm.note || "").trim(), date: new Date().toISOString(), done: false,
    };
    await persistFollowUps([...(person.follow_ups || []), newFu]);
    setLogForm({ kind: "email", direction: "outbound", outcome: "neutral", summary: "", note: "" });
    setShowLog(false);
    setSavingFu(false);
    toast.success("Interaction logged");
  };

  const toggleFollowUp = async (id) => {
    await persistFollowUps((person.follow_ups || []).map((it) => (it.id === id ? { ...it, done: !it.done } : it)));
  };
  const deleteFollowUp = async (id) => {
    await persistFollowUps((person.follow_ups || []).filter((it) => it.id !== id));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch md:items-center justify-center" onClick={onClose} data-testid="studio-profile">
      <div className="absolute inset-0" style={{ background: "rgba(3,4,10,0.82)", backdropFilter: "blur(10px)" }} />
      <div onClick={(e) => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-labelledby="studio-profile-title"
        className="relative w-full md:max-w-4xl mx-auto md:my-5 md:rounded-2xl overflow-hidden flex flex-col max-h-[100dvh] md:max-h-[92vh] shadow-2xl"
        style={{ "--studio-accent": "#f472b6", "--studio-accent-soft": "rgba(244,114,182,0.12)", background: "var(--gio-surface)", border: "1px solid rgba(244,114,182,0.35)", boxShadow: "0 0 34px rgba(236,72,153,0.12), var(--gio-shadow-lg)" }}>
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b" style={{ borderColor: "var(--gio-border)" }}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--studio-accent-soft)", color: "var(--studio-accent)", boxShadow: "0 0 18px rgba(236,72,153,0.14)" }}>
              <Building2 size={19} aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <div id="studio-profile-title" className="font-heading text-lg sm:text-xl font-bold truncate" style={{ color: "var(--gio-text)" }}>{person.stage_name}</div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <span className="inline-flex rounded-md px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider" style={{ background: "var(--studio-accent-soft)", color: "var(--studio-accent)" }}>
                  Studio / production partnership
                </span>
                {recordStatuses.map((status) => (
                  <span key={status} className="inline-flex rounded-md px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider" style={{ background: `${STATUS_HEX[status] || "#c084fc"}22`, color: STATUS_HEX[status] || "#c084fc" }}>
                    {STATUS_LABEL[status] || status}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={onEdit} data-testid="studio-profile-edit" className="btn-ghost inline-flex items-center gap-1.5 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-400">
              <Pencil size={13} aria-hidden="true" /> Edit
            </button>
            {canDelete && <button onClick={onDelete} data-testid="studio-profile-delete" aria-label="Delete studio record" title="Delete" className="btn-ghost p-2 text-[color:var(--gio-danger)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gio-danger)]">
              <Trash2 size={15} aria-hidden="true" />
            </button>}
            <button onClick={onClose} data-testid="studio-profile-close" aria-label="Close studio profile" title="Close" className="btn-ghost p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-400">
              <XIcon size={17} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-4 sm:px-6 py-4 sm:py-5">
            <OperationsPulse
              title="Studio Partnership Pulse"
              subtitle="Deal pipeline, productions, catalog output, and outreach status."
              accent="#f472b6"
              metrics={[
                { label: "Open deals", value: openDeals, color: "#c084fc", detail: `${deals.length} total` },
                { label: "Productions", value: sceneCount, color: "#f472b6", detail: "logged scenes" },
                { label: "Catalog items", value: catalogVideos.length, color: "#22d3ee", detail: "published/linked" },
                { label: "Open follow-ups", value: fuSorted.filter((f) => !f.done).length, color: "#fb7185", detail: "communications" },
              ]}
              gap="Per-shoot legal/ID verification and booking-calendar availability are not tracked at the studio-partnership level in the current CRM/backend model."
            />

            {/* Decision-makers / contacts */}
            <Section title="Contacts & Decision-Makers" icon={<Mail size={13} />}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-[12px]">
                {person.studio_contact_person && <InfoRow icon={<Building2 size={15} />} label="Primary contact" value={person.studio_contact_person} />}
                {person.studio_billing_email && <InfoRow icon={<Mail size={15} />} label="Billing email" value={person.studio_billing_email} link="email" />}
                {person.studio_website && <InfoRow icon={<Globe size={15} />} label="Website" value={person.studio_website} link="website" />}
                {person.studio_headquarters_address && <InfoRow icon={<MapPin size={15} />} label="HQ address" value={person.studio_headquarters_address} />}
                {person.email && <InfoRow icon={<Mail size={15} />} label="Email" value={person.email} link="email" />}
                {person.phone && <InfoRow icon={<Phone size={15} />} label="Phone" value={person.phone} link="phone" />}
              </div>
              {!person.studio_contact_person && !person.studio_billing_email && !person.email && !person.phone && (
                <EmptyState icon={<Mail size={16} />} text="No decision-maker contact on file yet." />
              )}
              {deals.some((d) => d.contact_name || d.contact_email) && (
                <div className="mt-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: "var(--gio-muted-2)" }}>Per-deal contacts</div>
                  <div className="space-y-1">
                    {deals.filter((d) => d.contact_name || d.contact_email).map((d) => (
                      <div key={d.id} className="text-[11px]" style={{ color: "var(--gio-muted-2)" }}>{d.studio_name}: {[d.contact_name, d.contact_email, d.contact_phone].filter(Boolean).join(" · ")}</div>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            {/* Contract / legal status */}
            <Section title="Contract & Legal Status" icon={<FileText size={13} />}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <StatusChip label="Contract template" ok={hasContract} okText="On file" missingText="No contract template linked" />
                <StatusChip label="Model release template" ok={hasRelease} okText="On file" missingText="No release template linked" />
              </div>
              <div className="text-[11px] mt-2" style={{ color: "var(--gio-muted-3)" }}>
                Per-performer ID/2257 verification is tracked on the individual performer record, not at the studio-partnership level.
              </div>
            </Section>

            {aiSuggestion && (
              <div className="rounded-xl border p-4 mb-5" style={{ borderColor: "rgba(244,114,182,0.32)", background: "var(--studio-accent-soft)" }} data-testid="studio-ai-suggestion">
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: "var(--studio-accent)" }}>
                  <Sparkles size={13} /> AI Manager suggestion
                </div>
                <div className="text-sm" style={{ color: "var(--gio-text)" }}>{aiSuggestion.reasoning}</div>
                <div className="text-[11px] mt-1" style={{ color: "var(--gio-muted-2)" }}>Priority: {aiSuggestion.priority} · Suggested tone: {aiSuggestion.suggested_tone}</div>
              </div>
            )}

            {/* Deal pipeline / shoot history & productions */}
            <Section title="Deal Pipeline & Productions" icon={<Building2 size={13} />}>
              {loadingDeals && <div className="text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>Loading…</div>}
              {!loadingDeals && deals.length === 0 && (
                <EmptyState icon={<Building2 size={17} />} text="No studio deals logged yet." detail="Open Studios to start a new collaboration linked to this CRM record." href="/studios" />
              )}
              {deals.map((d) => (
                <div key={d.id} className="border rounded-xl p-3 mb-2 transition-colors hover:border-[var(--gio-border-hi)]" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-2)" }}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="font-semibold text-sm" style={{ color: "var(--gio-text)" }}>{d.studio_name}</div>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: `${DEAL_STATUS_COLOR[d.status] || "#6b7280"}22`, color: DEAL_STATUS_COLOR[d.status] || "#6b7280" }}>
                      {DEAL_STATUS_LABEL[d.status] || d.status}
                    </span>
                  </div>
                  {(d.scenes || []).length === 0 && <div className="text-[11px] italic" style={{ color: "var(--gio-muted-3)" }}>No productions/scenes logged for this deal yet.</div>}
                  {(d.scenes || []).length > 0 && (
                    <div className="space-y-1">
                      {d.scenes.map((s) => (
                        <div key={s.id} className="flex items-center gap-2 text-[11px]" style={{ color: "var(--gio-muted-2)" }}>
                          <Film size={11} className="shrink-0" />
                          <span className="truncate flex-1">{s.name || "Untitled scene"}</span>
                          {s.date && <span>{s.date}</span>}
                          {Number(s.revenue) > 0 && <span style={{ color: "#22c55e" }}>${Number(s.revenue).toFixed(2)}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </Section>

            {/* Catalog items produced under this studio */}
            <Section title="Linked catalog items" icon={<Film size={13} />}>
              {loadingCatalog && <div className="text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>Loading…</div>}
              {!loadingCatalog && catalogVideos.length === 0 && (
                <EmptyState icon={<Film size={17} />} text="No catalog videos linked to this studio yet — not a failure, just not tagged." />
              )}
              {catalogVideos.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {catalogVideos.slice(0, 20).map((v) => (
                    <CatalogItem key={v.id} video={v} />
                  ))}
                </div>
              )}
            </Section>

            {/* Communication history */}
            <Section title={`Partnership Communications · ${fuSorted.length}`} icon={<MessageCircle size={13} />}
              action={<button onClick={() => setShowLog((v) => !v)} aria-expanded={showLog} aria-controls="studio-log-form" data-testid="studio-log-toggle" className="btn-ghost inline-flex items-center gap-1.5 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-400"><Plus size={13} aria-hidden="true" />Log</button>}>
              {showLog && (
                <div id="studio-log-form" className="mb-3 p-3 sm:p-4 rounded-xl border space-y-3" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-2)" }}>
                  <div className="flex gap-2 flex-wrap">
                    {FOLLOWUP_KINDS.map((k) => (
                      <button key={k.v} onClick={() => setLogForm((f) => ({ ...f, kind: k.v }))} type="button"
                        aria-pressed={logForm.kind === k.v} className="text-[10px] uppercase tracking-wider px-2.5 py-1.5 rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-400"
                        style={{ borderColor: logForm.kind === k.v ? "var(--gio-primary)" : "var(--gio-border)", color: logForm.kind === k.v ? "var(--gio-primary)" : "var(--gio-muted-2)" }}>
                        {k.label}
                      </button>
                    ))}
                  </div>
                  <input value={logForm.summary} onChange={(e) => setLogForm((f) => ({ ...f, summary: e.target.value }))}
                    placeholder="Subject / summary" data-testid="studio-log-summary" className="gio-input w-full" />
                  <textarea value={logForm.note} onChange={(e) => setLogForm((f) => ({ ...f, note: e.target.value }))}
                    placeholder="Details" rows={2} data-testid="studio-log-note" className="gio-input w-full" />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShowLog(false)} className="btn-ghost text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-400">Cancel</button>
                    <button onClick={addFollowUp} disabled={savingFu} data-testid="studio-log-save" className="btn-primary text-xs disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-400">Save</button>
                  </div>
                </div>
              )}
              {fuSorted.length === 0 && <EmptyState icon={<MessageCircle size={17} />} text="No partnership communications logged yet." />}
              <div className="space-y-1.5">
                {fuSorted.map((f) => {
                  const k = FOLLOWUP_KINDS.find((x) => x.v === f.kind) || FOLLOWUP_KINDS[0];
                  const dir = FU_DIRECTIONS.find((d) => d.v === f.direction);
                  const outcome = FU_OUTCOMES.find((o) => o.v === f.outcome);
                  return (
                    <div key={f.id} className="flex items-start gap-2.5 p-3 border rounded-lg transition-colors hover:border-[var(--gio-border-hi)]" style={{ borderColor: "var(--gio-border)" }}>
                      <button onClick={() => toggleFollowUp(f.id)} aria-label={f.done ? `Reopen ${f.summary}` : `Complete ${f.summary}`} className="mt-0.5 w-5 h-5 rounded-md border shrink-0 flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-400"
                        style={{ borderColor: f.done ? "#22c55e" : "var(--gio-border)", background: f.done ? "#22c55e" : "transparent" }}>
                        {f.done && <Check size={11} style={{ color: "#fff" }} />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className={`text-[12px] ${f.done ? "line-through" : ""}`} style={{ color: "var(--gio-text)" }}>{f.summary}</div>
                        <div className="text-[10px] mt-0.5" style={{ color: "var(--gio-muted-3)" }}>
                          {k.label} {dir && `· ${dir.label.split(" ")[0]}`} {outcome && `· ${outcome.label}`} · {formatRelative(f.date)}
                        </div>
                      </div>
                      <button onClick={() => deleteFollowUp(f.id)} aria-label={`Delete ${f.summary}`} title="Delete communication" className="btn-ghost p-1 text-[color:var(--gio-muted-2)] hover:text-[color:var(--gio-danger)] shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gio-danger)]"><Trash2 size={13} /></button>
                    </div>
                  );
                })}
              </div>
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon, action, children }) {
  return (
    <section className="mb-6">
      <div className="flex items-center justify-between gap-3 mb-3 border-b pb-2" style={{ borderColor: "var(--gio-border)" }}>
        <h2 className="inline-flex items-center gap-2 text-[12px] sm:text-[13px] font-bold" style={{ color: "var(--gio-text-2)" }}>
          <span className="inline-flex items-center justify-center rounded-md p-1.5" style={{ color: "var(--studio-accent)", background: "var(--studio-accent-soft)" }}>{icon}</span>
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function InfoRow({ icon, label, value, link }) {
  const href = link === "email" ? `mailto:${value}`
    : link === "phone" ? `tel:${value.replace(/[^\d+]/g, "")}`
      : link === "website" ? (/^https?:\/\//i.test(value) ? value : `https://${value}`)
        : null;
  return (
    <div className="flex min-w-0 items-start gap-3 rounded-xl border p-3 transition-colors hover:border-pink-400/40" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-2)" }}>
      <span className="mt-0.5 inline-flex shrink-0" style={{ color: "var(--studio-accent)" }}>{icon}</span>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--gio-muted-2)" }}>{label}</div>
        {href ? (
          <a href={href} target={link === "website" ? "_blank" : undefined} rel={link === "website" ? "noreferrer" : undefined} className="mt-0.5 inline-flex max-w-full items-center gap-1 break-all font-medium hover:text-pink-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-400" style={{ color: "var(--gio-text)" }}>
            {value}{link === "website" && <ExternalLink size={11} className="shrink-0" aria-hidden="true" />}
          </a>
        ) : (
          <div className="mt-0.5 break-words font-medium" style={{ color: "var(--gio-text)" }}>{value}</div>
        )}
      </div>
    </div>
  );
}

function StatusChip({ label, ok, okText, missingText }) {
  return (
    <div className="rounded-xl border p-3" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-2)" }}>
      <div className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--gio-muted-2)" }}>{label}</div>
      <div className="flex items-center gap-2 text-[12px]" style={{ color: ok ? "var(--gio-success)" : "var(--gio-muted)" }}>
        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: ok ? "var(--gio-success)" : "var(--gio-muted-2)" }} />
        <span>{ok ? okText : missingText}</span>
      </div>
    </div>
  );
}

function EmptyState({ icon, text, detail, href }) {
  return (
    <div className="flex flex-col items-start gap-2.5 rounded-xl border border-dashed p-4 sm:p-5" style={{ borderColor: "var(--gio-border-hi)", background: "var(--gio-surface-2)" }}>
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 shrink-0" style={{ color: "var(--studio-accent)" }}>{icon}</span>
        <div>
          <div className="text-[12px] font-medium" style={{ color: "var(--gio-text-2)" }}>{text}</div>
          {detail && <p className="mt-1 text-[11px] leading-relaxed" style={{ color: "var(--gio-muted)" }}>{detail}</p>}
        </div>
      </div>
      {href && <a href={href} className="btn-ghost inline-flex items-center gap-1.5 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-400"><ExternalLink size={12} aria-hidden="true" />Open Studios</a>}
    </div>
  );
}

function CatalogItem({ video }) {
  const thumbnail = video.cover_image
    || video.thumbnail_url
    || (video.mux_playback_id ? `https://image.mux.com/${video.mux_playback_id}/thumbnail.jpg?width=320&height=180&fit_mode=smartcrop` : null)
    || video.scene_photos?.[0];
  const status = video.edit_status?.replaceAll("_", " ") || (video.show_in_free_area ? "Published" : "Linked");

  return (
    <a href={`/video/${video.id}/preview`} data-testid={`studio-catalog-${video.id}`} className="group flex min-w-0 items-center gap-3 overflow-hidden rounded-xl border p-2 transition-all hover:-translate-y-0.5 hover:border-pink-400/50 hover:shadow-lg hover:shadow-pink-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-400" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-2)" }}>
      <div className="relative aspect-video w-24 sm:w-28 shrink-0 overflow-hidden rounded-lg" style={{ background: "var(--gio-surface-hi)" }}>
        {thumbnail ? <img src={thumbnail} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" /> : <div className="flex h-full items-center justify-center" style={{ color: "var(--gio-muted-2)" }}><ImageIcon size={19} aria-hidden="true" /></div>}
        {video.show_in_free_area && <span className="absolute left-1 top-1 rounded px-1.5 py-0.5 text-[8px] font-bold uppercase" style={{ background: "rgba(16,185,129,0.92)", color: "#04111B" }}>Published</span>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12px] font-semibold group-hover:text-pink-300" style={{ color: "var(--gio-text)" }}>{video.title || "Untitled video"}</div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px]" style={{ color: "var(--gio-muted)" }}>
          <span className="rounded-md px-1.5 py-0.5 capitalize" style={{ color: "var(--studio-accent)", background: "var(--studio-accent-soft)" }}>{status}</span>
          {video.shoot_date && <span>{video.shoot_date}</span>}
        </div>
      </div>
      <ExternalLink size={13} className="mr-1 shrink-0 opacity-50 transition-opacity group-hover:opacity-100" style={{ color: "var(--studio-accent)" }} aria-hidden="true" />
    </a>
  );
}
